import { AxiosResponse } from "axios";
import api from "../Interceptors/Interceptor";
import ApiResponse from "../Models/ApiResponse";
import ReferenceModel from "../Models/ReferenceModel";

export const SAVED_FILTER_VERSION = 1;

export const SAVED_FILTER_LOCATION = {
    DASHBOARD_VIEWER: "dashboard.viewer",
    DASHBOARD_VIEW: "dashboard.view",
    PROJECT_LIST: "project.list",
    PROJECT_SUPPORT: "project.support",
    ALERT_LIST: "alert.list",
    OBJECTIVE_LIST: "objective.list",
    PROCESSFLOW_LIST: "processflow.list",
    MARKETPLACE_CONNECTIONS: "marketplace.connections",
    MARKETPLACE_INTEGRATIONS: "marketplace.integrations",
} as const;

export type SavedFilterLocation = typeof SAVED_FILTER_LOCATION[keyof typeof SAVED_FILTER_LOCATION];

export interface SavedFilterConfiguration {
    version: number;
    values: Record<string, unknown>;
}

export interface ISavedFilter {
    uuid: string;
    name: string;
    location: string;
    locationIdentifier?: string | null;
    locationDisplayName?: string | null;
    configuration?: SavedFilterConfiguration | null;
    displayValues?: Record<string, string> | null;
    defaultFilter?: boolean;
    user?: ReferenceModel;
}

export interface IPostSavedFilter {
    name: string;
    location: string;
    locationIdentifier?: string | null;
    configuration: SavedFilterConfiguration;
    defaultFilter?: boolean;
}

export interface IPutSavedFilter {
    name?: string;
    configuration?: SavedFilterConfiguration;
}

export const logSavedFilterError = (action: string, error: unknown): void => {
    const responseData = (error as { response?: { data?: unknown } })?.response?.data;
    console.error(`Saved filter ${action} failed`, responseData ?? error);
};

export class SavedFilterService {
    public async listAll(): Promise<AxiosResponse<ApiResponse<ISavedFilter[]>>> {
        return api.get("v1/saved-filter");
    }

    public async list(
        location: string,
        locationIdentifier?: string | null,
    ): Promise<AxiosResponse<ApiResponse<ISavedFilter[]>>> {
        return api.get("v1/saved-filter", {
            params: {
                location,
                ...(locationIdentifier ? { locationIdentifier } : {}),
            },
        });
    }

    public async create(body: IPostSavedFilter): Promise<AxiosResponse<ApiResponse<ISavedFilter>>> {
        return api.post("v1/saved-filter", body);
    }

    public async update(uuid: string, body: IPutSavedFilter): Promise<AxiosResponse<ApiResponse<ISavedFilter>>> {
        return api.put(`v1/saved-filter/${uuid}`, body);
    }

    public async setDefault(uuid: string): Promise<AxiosResponse<ApiResponse<ISavedFilter>>> {
        return api.post(`v1/saved-filter/${uuid}/default`);
    }

    public async clearDefault(uuid: string): Promise<AxiosResponse<ApiResponse<ISavedFilter>>> {
        return api.delete(`v1/saved-filter/${uuid}/default`);
    }

    public async remove(uuid: string): Promise<AxiosResponse<ApiResponse<boolean>>> {
        return api.delete(`v1/saved-filter/${uuid}`);
    }
}
